import { Controller } from "@hotwired/stimulus"
import { allDeploymentStatusesStore } from "../../stores/deployment_statuses/all_deployment_statuses_store";
import { filteredItemsStore } from "../../stores/items/filtered_items_store"
import { devicesLoadingStore } from "../../stores/devices/devices_loading_store"
import { devicesBackgroundLoadStore } from "../../stores/devices/devices_background_load_store"
import { useStore } from "stimulus-store"
import { DEPLOYMENT_STATUS_FIELD, DEVICE_TYPE_FIELD, ID_FIELD, BUNDLE_TYPE_FIELD } from "constants/items_table_constants";
import TomSelect from "tom-select";
import { getCsrfToken } from "shared/form_helpers";
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications";
import { updateSelect } from "shared/selects/select_helpers";

// Connects to data-controller="table--items-table"
export default class extends Controller {

    static stores = [
        filteredItemsStore,
        allDeploymentStatusesStore,
        devicesLoadingStore,
        devicesBackgroundLoadStore
    ]

    static targets = [
        "table"
    ]

    static values = {
        tableDefinition: Array,
        disableStoreUpdates: Boolean
    }

    connect() {
        useStore(this)
        this.includeParts = false

        // Defer initialization to next microtask so that sibling controllers
        // (argus--table) on the same element have time to connect first.
        Promise.resolve().then(() => {
            this.initializeTable()
        })
    }

    /**
     * React to changes in the devices loading store
     */
    onDevicesLoadingStoreUpdate(isLoading) {
        const argusTable = this._getArgusTable()
        if (!argusTable) return

        if (isLoading) {
            // The initial autoLoad is a silent, greedy background prefetch. The
            // user hasn't requested data yet (they interact via Apply / area
            // selection), so we don't flash the overlay on page load. The Apply
            // path drives the overlay itself when the user is actually waiting.
            // User-initiated loads (area selection on Areas-edit) leave this
            // flag false and still surface the overlay here.
            if (this.devicesBackgroundLoadStoreValue === true) return

            argusTable.showLoading()
        } else {
            // Always clear on completion — safe even if we never showed it.
            argusTable.hideLoading()
        }
    }

    disconnect() {
        this.cleanupAllTomSelectInstances()
    }

    /**
     * React to changes in the filtered items store
     */
    onFilteredItemsStoreUpdate() {
        if (this.disableStoreUpdatesValue) {
            return
        }
        this.updateTableData()
    }

    /**
     * React to changes in the deployment statuses store
     */
    onAllDeploymentStatusesStoreUpdate() {
        if (this.hasTableTarget && this.allDeploymentStatusesStoreValue) {
            const deploymentStatusOptions = this.allDeploymentStatusesStoreValue.map(status => ({
                value: status.id,
                name: status.name
            }))

            document.querySelectorAll('.tom-select').forEach((element) => {
                updateSelect(element.id, deploymentStatusOptions);
            });
        }
    }

    // ── Argus Table helpers ────────────────────────────────────────

    /**
     * Get the argus--table controller instance from the table target element.
     */
    _getArgusTable() {
        if (!this.hasTableTarget) return null
        return this.application.getControllerForElementAndIdentifier(
            this.tableTarget, 'argus--table'
        )
    }

    // ── Table initialization ───────────────────────────────────────

    /**
     * Initialize the table via argus--table.
     */
    initializeTable() {
        if (!this.hasTableTarget) {
            console.warn('table--items-table: table target not found')
            return
        }

        const argusTable = this._getArgusTable()
        if (!argusTable) {
            console.warn('table--items-table: argus--table controller not found')
            return
        }

        // Build columns with formatters
        const columns = this.buildColumns(this.tableDefinitionValue)

        // Set columns on the argus--table controller, hiding the ID column
        argusTable._columns = columns.map(col => ({
            ...col,
            visible: col.field !== ID_FIELD,
        }))

        // Rebuild the column toggler with the new columns
        const ColumnTogglerClass = argusTable.columnToggler.constructor
        argusTable.columnToggler = new ColumnTogglerClass(
            argusTable._columns, () => argusTable._render()
        )

        // Set up row attributes function
        argusTable._rowAttributesFn = (row, index) => {
            const isBundle = row.bundle_instance_id ? true : false
            const itemType = isBundle ? 'bundle' : 'device'
            const itemId = row.id || row[ID_FIELD] || ''
            const bundleInstanceId = row.bundle_instance_id || ''
            const bundleId = row.bundle_id || row.bundle?.id || ''
            const itemName = row.name || itemId

            return {
                'data-item-id': itemId,
                'data-bundle-instance-id': bundleInstanceId,
                'data-bundle-id': bundleId,
                'data-action': 'dblclick->table--items-table#navigateToDevice keydown->table--items-table#handleRowKeydown',
                'tabindex': '0',
                'role': 'button',
                'aria-label': `Double-click or press Enter to edit ${itemType} ${itemName}`,
                'style': 'cursor: pointer;'
            }
        }

        // Configure detail view if the manager exists
        // On manage devices and reports pages, the detail icon column shows the bundle icon
        // and double-clicking it navigates to the bundle page (no expand/collapse).
        // On the manage bundles page (bundle-instances-table), keep the expandable detail view.
        if (argusTable.detailViewManager) {
            const isBundleInstancesTable = this.tableTarget.classList.contains('bundle-instances-table')
            if (isBundleInstancesTable) {
                argusTable.detailViewManager._detailFilter = (row) => {
                    return row.bundle_instance_id ? true : false
                }
                argusTable.detailViewManager._detailFormatter = (row) => {
                    return this.bundleDetailFormatter(0, row)
                }
            } else {
                // Show the icon for bundle rows but navigate on double-click instead of expanding
                argusTable.detailViewManager._detailFilter = (row) => {
                    return row.bundle_instance_id ? true : false
                }
                argusTable.detailViewManager._detailFormatter = () => ''

                // Override toggleRow to prevent expand/collapse behavior on single click
                // Navigation happens on double-click (added in _replaceBundleDetailIcons)
                argusTable.detailViewManager.toggleRow = (rowIndex, row, parentTr) => {
                    // No-op: prevent expand/collapse behavior
                }

                // Also override _onDetailToggle as a safety net
                argusTable._onDetailToggle = (index, row, tr) => {
                    // No-op: prevent expand/collapse behavior
                }
            }
        }

        // Register post-render callback to re-initialize TomSelects after every render
        argusTable._onAfterRender = () => {
            this.cleanupAllTomSelectInstances()
            this.initializeAllTomSelects()
            this._replaceBundleDetailIcons()
        }

        // Register editable save handler for inline cell editing
        argusTable._onEditableSave = ({ field, oldValue, newValue, row, index }) => {
            this._handleEditableSave(field, oldValue, newValue, row, index)
        }

        // Trigger initial render
        argusTable._render()

    }

    /**
     * Build columns configuration from table definition
     */
    buildColumns(tableDefinition) {
        const columnDefinitions = [];

        tableDefinition.forEach((columnDef) => {
            const column = { ...columnDef };

            if (column.field === DEPLOYMENT_STATUS_FIELD && column.editable === true) {
                column.formatter = (value, row, index) => this.deploymentStatusSelectFormatter(value, row, index)
            }

            if (column.field === DEVICE_TYPE_FIELD) {
                column.formatter = (value, row, index) => this.deviceTypeColumnFormatter(value, row, index)
            }

            if (column.field === BUNDLE_TYPE_FIELD) {
                column.formatter = (value, row, index) => this.bundleTypeColumnFormatter(value, row, index)
            }

            columnDefinitions.push(column);
        });

        return columnDefinitions;
    }

    /**
     * Prepare table data from filtered items store
     */
    prepareTableData() {
        const devices = this.filteredItemsStoreValue || []
        const rows = devices.map(device => ({
            ...device,
            deviceName: device.name,
            deviceTypeName: device.device?.device_type?.name || device.part?.part_type?.name || '',
            deploymentStatusName: device.deployment_status?.name,
            locationPath: this.buildLocationPath(device)
        }))

        // For bundle items, the server returns the bundle definition name as device_type.name.
        // Fetch the real device types from the bundle instance API and update the rows.
        this._resolveBundleDeviceTypes(rows)

        return rows
    }

    async _resolveBundleDeviceTypes(rows) {
        const bundleRows = rows.filter(r => r.bundle_instance_id && r.bundle?.id)
        if (bundleRows.length === 0) return

        const seen = new Set()
        for (const row of bundleRows) {
            const key = `${row.bundle.id}_${row.bundle_instance_id}`
            if (seen.has(key)) continue
            seen.add(key)

            try {
                const { getDevicesInBundleInstance } = await import('shared/devices/device_discovery')
                const allItems = await getDevicesInBundleInstance(row.bundle.id, row.bundle_instance_id)

                // Build a map of item ID -> real device type name from the bundle instance response
                const deviceTypeMap = {}
                for (const item of allItems) {
                    if (item.device?.device_type?.name) {
                        deviceTypeMap[item.id] = item.device.device_type.name
                    }
                }

                // Update the internal data rows directly
                const argusTable = this._getArgusTable()
                if (!argusTable) continue

                let updated = false
                for (const internalRow of argusTable.dataManager._rows) {
                    if (deviceTypeMap[internalRow.id] && internalRow.deviceTypeName !== deviceTypeMap[internalRow.id]) {
                        internalRow.deviceTypeName = deviceTypeMap[internalRow.id]
                        updated = true
                    }
                }
                if (updated) argusTable._render()
            } catch (e) {
                // Silently fail — bundle name stays as fallback
            }
        }
    }

    /**
     * Build location path string
     */
    buildLocationPath(device) {
        const parts = []
        if (device.locale?.region?.name) parts.push(device.locale.region.name)
        if (device.locale?.building?.name) parts.push(device.locale.building.name)
        if (device.locale?.area?.name) parts.push(device.locale.area.name)
        if (device.locale?.room?.name) parts.push(device.locale.room.name)
        return parts.join(' > ')
    }

    /**
     * Update table data when store changes
     */
    updateTableData() {
        if (!this.hasTableTarget) return

        this.cleanupAllTomSelectInstances()

        const newData = this.prepareTableData()
        const argusTable = this._getArgusTable()
        if (argusTable) {
            argusTable.loadData(newData)
        }
        // Re-initialize TomSelects after render
        requestAnimationFrame(() => {
            this.initializeAllTomSelects()
        })
    }

    /**
     * Handle deployment status change
     */
    async handleDeploymentStatusChange(rowId, newValue) {
        try {
            const response = await fetch(`/api/items/${rowId}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({
                    id: rowId,
                    deployment_status: newValue
                })
            })

            if (response.ok) {
                const data = await response.json()
                const deviceName = data.data?.items?.[0]?.name || 'Device'
                sendSuccessNotification(`Updated ${deviceName}'s deployment status`)
            } else {
                sendFailureNotification('Unable to update deployment status. Please try again.')
            }
        } catch (error) {
            console.error('Error updating deployment status:', error)
            sendFailureNotification('Unable to update deployment status. Please try again.')
        }
    }

    /**
     * Handle inline editable save from argus--table
     */
    async _handleEditableSave(field, oldValue, newValue, row, index) {
        const id = row.id
        try {
            const response = await fetch(`/api/items/${id}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({ id, [field]: newValue })
            })

            if (response.ok) {
                const data = await response.json()
                const deviceName = data.data?.items?.[0]?.name || 'Device'
                sendSuccessNotification(`Updated ${deviceName}'s ${field}`)

                // Update the row in the data store so re-renders reflect the new value
                const argusTable = this._getArgusTable()
                if (argusTable) {
                    argusTable.updateRow(id, { ...row, [field]: newValue })
                }
            } else {
                sendFailureNotification(`Unable to update ${field}. Please try again.`)
                // Re-render to revert the optimistic DOM update back to the store value
                const argusTable = this._getArgusTable()
                if (argusTable) {
                    argusTable._render()
                }
            }
        } catch (error) {
            console.error('Error updating device:', error)
            sendFailureNotification(`Unable to update ${field}. Please try again.`)
            // Re-render to revert the optimistic DOM update back to the store value
            const argusTable = this._getArgusTable()
            if (argusTable) {
                argusTable._render()
            }
        }
    }

    handleEditDevice(device) {
        this.dispatch('editDevice', {
            detail: { device: device },
            bubbles: true
        })
    }

    handleDeleteDevice(device) {
        this.dispatch('deleteDevice', {
            detail: { device: device },
            bubbles: true
        })
    }

    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }

    /**
     * Initialize TomSelect on all deployment status selects in the table
     */
    initializeAllTomSelects() {
        if (!this.hasTableTarget) return

        const selects = this.tableTarget.querySelectorAll('select.deployment-status-select')
        selects.forEach(selectElement => {
            if (!selectElement._tomSelectInstance) {
                this.initializeDeploymentStatusTomSelect(selectElement)
            }
        })
    }

    initializeDeploymentStatusTomSelect(selectElement) {
        if (selectElement._tomSelectInstance) return

        try {
            const rowId = selectElement.dataset.rowId

            const tomSelectInstance = new TomSelect(selectElement, {
                create: false,
                allowEmptyOption: false,
                placeholder: 'Select Status',
                maxItems: 1,
                closeAfterSelect: true,
                hideSelected: false,
                dropdownParent: 'body',
                onChange: (value) => {
                    this.handleDeploymentStatusChange(rowId, value)
                }
            })

            selectElement._tomSelectInstance = tomSelectInstance
        } catch (error) {
            console.error('Error initializing TomSelect:', error)
        }
    }

    cleanupAllTomSelectInstances() {
        if (!this.hasTableTarget) return

        const selects = this.tableTarget.querySelectorAll('select.deployment-status-select')
        selects.forEach(select => {
            if (select._tomSelectInstance) {
                try {
                    select._tomSelectInstance.destroy()
                    delete select._tomSelectInstance
                } catch (error) {
                    console.error('Error cleaning up TomSelect:', error)
                }
            }
        })
    }

    /**
     * Replace the default ▶ detail toggle icons with bundle icons for bundle rows.
     * Adds double-click navigation to the bundle page.
     * Only applies on manage devices and reports pages (not manage bundles).
     */
    _replaceBundleDetailIcons() {
        if (!this.hasTableTarget) return
        if (this.tableTarget.classList.contains('bundle-instances-table')) return

        const tbody = this.tableTarget.querySelector('tbody')
        if (!tbody) return

        const trs = tbody.querySelectorAll('tr:not(.argus-detail-row)')
        trs.forEach((tr) => {
            const bundleInstanceId = tr.dataset.bundleInstanceId
            if (!bundleInstanceId || bundleInstanceId === '' || bundleInstanceId === 'undefined') return

            const toggleTd = tr.querySelector('.argus-detail-toggle')
            if (!toggleTd) return

            const oldIcon = toggleTd.querySelector('.argus-detail-icon')
            if (!oldIcon) return

            // Find the row data to get the bundle icon
            const bundleId = tr.dataset.bundleId
            const allRows = this._getArgusTable()?.dataManager?.getAll() || []
            const row = allRows.find(r => r.bundle_instance_id === bundleInstanceId)
            const bundleIcon = row?.bundle?.icon

            // Replace the old icon element entirely (removes the renderer's click listener)
            const newIcon = document.createElement('span')
            newIcon.className = 'argus-detail-icon bundle-nav-icon'
            newIcon.innerHTML = this.formatBundleIcon(bundleIcon)
            newIcon.title = 'Double-click to view bundle'
            newIcon.style.cursor = 'pointer'

            // Add double-click handler to navigate to the bundle page
            newIcon.addEventListener('dblclick', (e) => {
                e.stopPropagation()
                if (bundleInstanceId && bundleId) {
                    window.location.href = `/bundles/update?id=${bundleInstanceId}&bundle=${bundleId}`
                }
            })

            oldIcon.replaceWith(newIcon)
        })
    }

    refresh() {
        this.updateTableData()
    }

    clear() {
        if (!this.hasTableTarget) return
        const argusTable = this._getArgusTable()
        if (argusTable) argusTable.clearData()
    }

    getData() {
        if (!this.hasTableTarget) return []
        const argusTable = this._getArgusTable()
        return argusTable ? argusTable.getData() : []
    }

    updateTableDefinition(newDefinition) {
        this.tableDefinitionValue = newDefinition
        this.initializeTable()
    }

    /**
     * Navigate to device or bundle update page on double-click
     */
    navigateToDevice(event) {
        const table = this.tableTarget
        if (table && table.dataset.disableNavigation === 'true') {
            return
        }

        const target = event.target
        const isInteractiveElement = target.closest('button, a, select, input, .ts-wrapper, .ts-control, .ts-dropdown, .detail-icon, .argus-detail-toggle, .argus-detail-icon')

        if (isInteractiveElement) {
            return
        }

        const row = event.currentTarget
        const itemId = row.dataset.itemId
        const bundleInstanceId = row.dataset.bundleInstanceId
        const bundleId = row.dataset.bundleId

        if (bundleInstanceId && bundleInstanceId.trim() && bundleInstanceId !== 'undefined') {
            if (bundleId && bundleId.trim() && bundleId !== 'undefined') {
                window.location.href = `/bundles/update?id=${bundleInstanceId}&bundle=${bundleId}`
            } else {
                console.warn('Bundle instance found but bundle_id is missing. Cannot navigate.', {
                    bundleInstanceId: bundleInstanceId,
                    itemId: itemId
                })
            }
        } else if (itemId && itemId.trim() && itemId !== 'undefined') {
            window.location.href = `/devices/update?device=${itemId}`
        } else {
            console.warn('No valid ID found for navigation. Item ID:', itemId, 'Bundle Instance ID:', bundleInstanceId)
        }
    }

    handleRowKeydown(event) {
        if (event.key !== 'Enter') return
        event.preventDefault()
        this.navigateToDevice(event)
    }

    /*
     * FORMATTERS
     */
    deviceTypeColumnFormatter(value, row, index) {
        if (row?.bundle_instance_id) {
            if (row?.bundle?.name) {
                return row.bundle.name;
            } else {
                return 'Bundle (Type Unknown)';
            }
        }

        if (row?.bundle?.id && !row?.bundle_instance_id) {
            const deviceTypeName = row?.device?.device_type?.name;
            const partTypeName = row?.part?.part_type?.name;
            return deviceTypeName || partTypeName || value;
        }

        const deviceTypeName = row?.device?.device_type?.name;
        const partTypeName = row?.part?.part_type?.name;

        return deviceTypeName || partTypeName || value || '-';
    }

    bundleTypeColumnFormatter(value, row, index) {
        if (!value) return '';

        const bundleIcon = row.bundle?.icon;

        if (bundleIcon) {
            const formattedIcon = this.formatBundleIcon(bundleIcon);
            return `${formattedIcon} <span class="ms-2">${value}</span>`;
        }

        return value;
    }

    deploymentStatusSelectFormatter(value, row, index) {
        const deploymentStatusName = row.deployment_status?.name;
        const deploymentStatusId = row.deployment_status?.id;

        const selectElement = document.createElement("select");
        selectElement.className = "deployment-status-select";
        selectElement.dataset.rowId = row.id;
        selectElement.dataset.field = DEPLOYMENT_STATUS_FIELD;

        const deploymentStatuses = this.allDeploymentStatusesStoreValue || []

        for (const deploymentStatus of deploymentStatuses) {
            const option = document.createElement("option");
            option.value = deploymentStatus.id;
            option.text = deploymentStatus.name;
            option.dataset.color = deploymentStatus.status_color;

            if (deploymentStatus.id === deploymentStatusId) {
                option.setAttribute("selected", "selected")
            }

            selectElement.appendChild(option);
        }

        return selectElement.outerHTML;
    }

    formatBundleIcon(iconData) {
        if (!iconData || iconData.trim() === '') {
            return '<i class="icon-drawer3"></i>';
        }

        if (iconData.includes('<') && iconData.includes('>')) {
            return iconData;
        }

        return `<i class="${iconData}"></i>`;
    }

    /**
     * Format the detail view for bundle instances
     */
    bundleDetailFormatter(index, row) {
        if (!row.bundle_instance_id) return ''

        // First check if we already have all components in the store (e.g., reports page)
        const allItems = this.filteredItemsStoreValue || []
        const storeComponents = allItems.filter(item =>
            item.bundle?.id === row.bundle?.id &&
            item.bundle_instance_id === row.bundle_instance_id
        )

        // Check if we have parts in the store (manage devices only loads type=devices)
        const hasParts = storeComponents.some(item => item.part?.part_type)

        if (hasParts || !row.bundle?.id) {
            // Store has complete data (parts included) — render directly
            return this.renderBundleComponents(storeComponents, row)
        }

        // Store only has devices — fetch full bundle instance from API to get parts too
        const containerId = `bundle-detail-${row.bundle_instance_id}-${index}`
        const bundleName = this.escapeHtml(row.bundle?.name || row.name || 'Bundle')

        // Return a loading placeholder; async fetch will replace it
        setTimeout(() => {
            this._fetchAndRenderBundleComponents(containerId, row, storeComponents)
        }, 0)

        return `<div id="${containerId}" class="bundle-components-detail p-3">
            <h6 class="mb-3">${bundleName}</h6>
            <div class="text-muted"><i class="bi bi-hourglass-split me-2"></i>Loading components...</div>
        </div>`
    }

    async _fetchAndRenderBundleComponents(containerId, row, storeComponents) {
        try {
            const { getDevicesInBundleInstance } = await import('shared/devices/device_discovery')
            const allBundleItems = await getDevicesInBundleInstance(row.bundle.id, row.bundle_instance_id)

            const container = document.getElementById(containerId)
            if (container) {
                container.outerHTML = this.renderBundleComponents(allBundleItems, row)
            }
        } catch (error) {
            console.error('Error fetching bundle components:', error)
            const container = document.getElementById(containerId)
            if (container) {
                // Fall back to store data (devices only)
                container.outerHTML = this.renderBundleComponents(storeComponents, row)
            }
        }
    }

    renderBundleComponents(components, parentRow) {
        if (!components || components.length === 0) {
            return `
                <div class="alert alert-info m-3">
                    <i class="fas fa-info-circle me-2"></i>
                    This bundle instance has no components.
                </div>
            `
        }

        const bundleName = this.escapeHtml(parentRow.bundle?.name || parentRow.name || 'Bundle')

        let html = `
            <div class="bundle-components-detail p-3">
                <h6 class="mb-3">${bundleName} (${components.length} components)</h6>
                <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                        <thead class="table-light">
                            <tr>
                                <th>Type</th>
                                <th>Name</th>
                                <th>Manufacturer</th>
                                <th>Model / Part Type</th>
                                <th>Cost</th>
                            </tr>
                        </thead>
                        <tbody>
        `

        components.forEach(component => {
            const isDevice = component.device && component.device.device_type
            const isPart = component.part && component.part.part_type

            let type, name, manufacturer, modelOrPartType, cost

            if (isDevice) {
                type = 'Device'
                name = this.escapeHtml(component.device.device_type.name || '-')
                manufacturer = this.escapeHtml(component.device.manufacturer?.name || '-')
                modelOrPartType = this.escapeHtml(component.device.model?.name || '-')
                cost = component.device.model?.cost || '-'
            } else if (isPart) {
                type = 'Part'
                name = this.escapeHtml(component.part.part_type.name || '-')
                manufacturer = '-'
                modelOrPartType = '-'
                cost = component.part.part_type.cost || '-'
            } else {
                type = 'Unknown'
                name = this.escapeHtml(component.name || '-')
                manufacturer = '-'
                modelOrPartType = '-'
                cost = '-'
            }

            const typeBadgeClass = isDevice ? 'bg-primary' : isPart ? 'bg-secondary' : 'bg-secondary'

            html += `
                <tr>
                    <td><span class="badge ${typeBadgeClass}">${type}</span></td>
                    <td>${name}</td>
                    <td>${manufacturer}</td>
                    <td>${modelOrPartType}</td>
                    <td>${cost}</td>
                </tr>
            `
        })

        html += `
                        </tbody>
                    </table>
                </div>
            </div>
        `

        return html
    }

    isPartRow(row) {
        return row.part && row.part.part_type && !row.device
    }
}
